<template>
  <v-form
    ref="form"
    v-model="valid"
    lazy-validation
    @submit.prevent="onConfirm"
  >
    <v-card tile>
      <v-card-text>
        <v-row no-gutters>
          <v-col cols="3">
            <v-row no-gutters>
              <v-col>
                <div class="text-overline">Tåg</div>
                <h2>{{ trainNumber }}</h2>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <DuwaEditModalListSelected :selected="selected" />
              </v-col>
            </v-row>
          </v-col>
          <v-divider vertical></v-divider>
          <v-col cols="3">
            <v-tabs vertical v-model="activeTab">
              <v-tabs-slider color="primary"></v-tabs-slider>
              <v-tab
                v-for="(tab, $i) in tabs"
                :key="$i"
                :disabled="tab.name.includes('Via') && selected.length > 1"
              >
                <div class="flex">
                  <div>
                    {{ tab.name }}
                  </div>
                  <div
                    v-if="tab.name.includes('Via') && selected.length > 1"
                    class="text-caption"
                    style="text-transform: lowercase"
                  >
                    Endast om 1 avgång är markerad
                  </div>
                </div>
              </v-tab>
            </v-tabs>
          </v-col>
          <v-divider vertical></v-divider>
          <v-col>
            <template v-if="!$apollo.loading">
              <v-tabs-items v-model="activeTab">
                <v-tab-item v-for="(tab, $i) in tabs" :key="$i">
                  <component :codes="duwaCodes" :is="tab.component" />
                </v-tab-item>
              </v-tabs-items>
            </template>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer />
        <v-btn rounded @click="closeModal">Avbryt</v-btn>
        <v-btn rounded type="submit" color="primary" :loading="pending">
          Skicka ändringar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import { required } from '@/common/validationRules'
import DuwaEditModalListSelected from './DuwaEditModalListSelected'
import DUWA_CODES_QUERY from '../graphql/duwaCodes'
import MUTATION_UPDATE_DUWA from '../graphql/updateDuwa'
import DuwaProductinformation from './form/DuwaProductinformation'
import DuwaServices from './form/DuwaServices'
import DuwaDeviations from './form/DuwaDeviations'
import DuwaBookings from './form/DuwaBookings'
import DuwaOtherInformation from './form/DuwaOtherInformation'
import DuwaTrainCompositions from './form/DuwaTrainCompositions'
import DuwaViaFrom from './form/DuwaViaFrom'
import DuwaViaTo from './form/DuwaViaTo'
import { get } from 'vuex-pathify'

export default {
  name: 'DuwaEditModal',

  components: {
    DuwaEditModalListSelected
  },

  props: {
    trainNumber: String
  },

  apollo: {
    duwaCodes: {
      query: DUWA_CODES_QUERY,
      fetchPolicy: 'no-cache'
    }
  },

  data: () => ({
    required,
    valid: true,
    pending: false,
    tabs: [
      { name: 'Produktinformation', component: DuwaProductinformation },
      { name: 'Service', component: DuwaServices },
      { name: 'Avvikelse', component: DuwaDeviations },
      { name: 'Bokning', component: DuwaBookings },
      { name: 'Övrigt', component: DuwaOtherInformation },
      { name: 'Tågsammansättning', component: DuwaTrainCompositions },
      { name: 'Via från station', component: DuwaViaFrom },
      { name: 'Via till station', component: DuwaViaTo }
    ],
    activeTab: null
  }),

  created() {
    this.$store.set(
      'duwa/payload',
      this.selected.map(x => ({
        activity_id: x.activity_id,
        productinformation: {
          before: (x.product_information || []).map(({ code }) => ({ code })),
          after: this.currentProductinformation.map(code => ({ code }))
        },
        service: {
          before: (x.service || []).map(({ code }) => ({ code })),
          after: this.currentServices.map(code => ({ code }))
        },
        deviation: {
          before: (x.deviation || []).map(({ code, parameters }) => {
            const data = {
              code,
              parameters: []
            }

            if (parameters) {
              data.parameters = parameters.map(({ name, value }) => ({
                name,
                value
              }))
            }

            return data
          }),
          after: this.currentDeviations.map(x => ({
            ...x
          }))
        },
        booking: {
          before: (x.booking || []).map(({ code }) => ({ code })),
          after: this.currentBookings.map(code => ({ code }))
        },
        other: {
          before: (x.other_information || []).map(({ code, parameters }) => {
            const data = {
              code,
              parameters: []
            }

            if (parameters) {
              data.parameters = parameters.map(({ name, value }) => ({
                name,
                value
              }))
            }

            return data
          }),
          after: this.currentOtherInformation.map(x => ({
            ...x
          }))
        },
        traincomposition: {
          before: (x.train_composition || []).map(({ code, parameters }) => {
            const data = {
              code,
              parameters: []
            }

            if (parameters) {
              data.parameters = parameters.map(({ name, value }) => ({
                name,
                value
              }))
            }

            return data
          }),
          after: this.currentTrainCompositions.map(x => ({
            ...x
          }))
        },
        viafromlocation: {
          before: x.via_from_location || [],
          after:
            this.selected.length === 1
              ? this.currentViaFromLocation
              : x.via_from_location || []
        },
        viatolocation: {
          before: x.via_to_location || [],
          after:
            this.selected.length === 1
              ? this.currentViaToLocation
              : x.via_to_location || []
        }
      }))
    )
  },

  computed: {
    currentProductinformation: get('duwa/currentProductinformation'),
    currentServices: get('duwa/currentServices'),
    currentDeviations: get('duwa/currentDeviations'),
    currentBookings: get('duwa/currentBookings'),
    currentOtherInformation: get('duwa/currentOtherInformation'),
    currentTrainCompositions: get('duwa/currentTrainCompositions'),
    currentViaFromLocation: get('duwa/currentViaFromLocation'),
    currentViaToLocation: get('duwa/currentViaToLocation'),
    selected: get('duwa/selected'),
    activities: get('duwa/payload'),
    advertised_train_ident: get('duwa/advertised_train_ident'),
    scheduled_departure_date_time: get('duwa/scheduled_departure_date_time')
  },

  methods: {
    async onConfirm() {
      const isValidForm = await this.$refs.form.validate()
      if (!isValidForm) return

      this.pending = true

      const payload = {
        advertised_train_ident: this.advertised_train_ident,
        scheduled_departure_date_time: this.scheduled_departure_date_time,
        activities: this.activities.map(x => {
          return Object.keys(x).reduce((acc, key) => {
            if (key === 'activity_id') {
              acc[key] = x[key]
              return acc
            }
            const value = {
              before: x[key].before.map(v => {
                delete v.__typename

                if (v.parameters && !v.parameters.length) {
                  delete v.parameters
                }

                return v
              }),
              after: x[key].after.map(v => {
                delete v.__typename

                if (v.parameters && !v.parameters.length) {
                  delete v.parameters
                }

                return v
              })
            }
            if (key === 'viafromlocation') {
              acc['via_from_location'] = value
            } else if (key === 'viatolocation') {
              acc['via_to_location'] = value
            } else if (key === 'traincomposition') {
              acc['train_composition'] = value
            } else if (key === 'productinformation') {
              acc['product_information'] = value
            } else {
              acc[key] = value
            }

            return acc
          }, {})
        })
      }

      await this.$apollo.mutate({
        mutation: MUTATION_UPDATE_DUWA,
        variables: {
          payload
        }
      })

      this.closeModal()
    },

    closeModal() {
      this.$emit('close')
      this.$store.set('duwa/payload', [])
    }
  }
}
</script>
